import { NgModule } from '@angular/core';
import { NoPreloading, PreloadAllModules, RouterModule, Routes, UrlMatcher, UrlMatchResult, UrlSegment } from '@angular/router';
import { environment } from '@environments/environment';
import { authCanActivate } from '@guards/auth.guard';
import { disputeCanActivate } from '@task/dispute/dispute.guard';
import { mobileAppCanActivate } from '@pyjam-animate/mobile-app.guard';
import { avatarLocationCanActivate } from '@avatar/guards/avatar-location.guard';
import { audioCallCanActivate, audioCallCanDeactivate } from '@call/pages/audio-call/audio-call.guard';
import { isStoredNicknamePatternValid } from '@utils/nickname';

const nicknameMatcher: UrlMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (segments.length !== 1) return null;
  const path = segments[0].path;
  if (!path.startsWith('@') || path.length < 2) return null;

  const nickname = path.slice(1);
  if (!isStoredNicknamePatternValid(nickname)) return null;
  return {
    consumed: segments,
    posParams: {
      nickname: new UrlSegment(nickname, {})
    }
  };
};

type MainMenuRoute = '/avatar' | '/chats' | '/calls';

const mainMenuRouteStorageKey: string = 'mainMenuRoute';

const resolveStoredMainMenuRoute = (): MainMenuRoute | null => {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const savedRoute: string | null = localStorage.getItem(mainMenuRouteStorageKey);
  if (savedRoute === '/avatar' || savedRoute === '/chats' || savedRoute === '/calls') {
    return savedRoute;
  }
  return null;
};

const rootToChatsMatcher: UrlMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (segments.length !== 0) {
    return null;
  }
  return resolveStoredMainMenuRoute() === '/chats'
    ? {consumed: segments}
    : null;
};

const rootToCallsMatcher: UrlMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (segments.length !== 0) {
    return null;
  }
  return resolveStoredMainMenuRoute() === '/calls'
    ? {consumed: segments}
    : null;
};

const appRoutes: Routes = [
  {
    matcher: rootToChatsMatcher,
    redirectTo: 'chats',
  },
  {
    matcher: rootToCallsMatcher,
    redirectTo: 'calls',
  },
  {
    path: '',
    redirectTo: 'avatar',
    pathMatch: 'full'
  },
  {
    path: 'pyjam-animate',
    loadComponent: () => import('./features/pyjam-animate/pyjam-animate.component').then(c => c.PyjamAnimateComponent),
    canActivate: [mobileAppCanActivate],
  },
  {
    path: 'login',
    loadChildren: () => import('./features/login/login.routes').then(r => r.loginRoutes)
  },
  {
    path: 'registration',
    loadChildren: () => import('./features/registration/registration.routes').then(r => r.registrationRoutes),
    canActivate: [authCanActivate, disputeCanActivate],
  },
  {
    path: 'tasks',
    loadChildren: () => import('./features/task/task.routes').then(r => r.taskRoutes),
  },
  {
    path: 'people',
    loadChildren: () => import('./features/people/people.routes').then(r => r.peopleRoutes),
  },
  {
    path: 'notifications',
    loadComponent: () => import('./features/notifications/notifications.component').then(c => c.NotificationsComponent),
    canActivate: [disputeCanActivate],
    data: {showMenu: true, title: 'seo.title.notifications', description: 'seo.description.notifications'},
  },
  {
    path: 'chats',
    loadChildren: () => import('./features/chat/chat.routes').then(r => r.chatRoutes),
    canActivate: [disputeCanActivate],
  },
  {
    path: 'calls',
    loadComponent: () => import('./features/call/pages/call-log/call-log.component').then(c => c.CallLogComponent),
    canActivate: [disputeCanActivate],
    data: {showMenu: true, title: 'seo.title.call', description: 'seo.description.call'},
  },
  {
    path: 'incoming-call/:orderId',
    loadComponent: () => import('./features/call/pages/incoming-call/incoming-call.component').then(c => c.IncomingCallComponent),
    data: {showMenu: false},
  },
  {
    path: 'audio-call',
    loadComponent: () => import('./features/call/pages/audio-call/audio-call.component').then(c => c.AudioCallComponent),
    canActivate: [authCanActivate, audioCallCanActivate],
    canDeactivate: [audioCallCanDeactivate],
    data: {showMenu: false},
  },
  {
    path: 'profile',
    loadChildren: () => import('./features/profile/profile.routes').then(r => r.profileRoutes),
    canActivate: [disputeCanActivate],
  },
  {
    path: 'wallet',
    loadChildren: () => import('./features/wallet/wallet.routes').then(r => r.walletRoutes),
    canActivate: [authCanActivate, disputeCanActivate],
  },
  {
    path: 'avatar',
    canActivate: [avatarLocationCanActivate],
    loadChildren: () => import('./features/avatar/avatar.routes').then(r => r.avatarRoutes)
  },
  {
    path: 'reply',
    loadChildren: () => import('./features/reply/reply.routes').then(r => r.replyRoutes),
    canActivate: [authCanActivate, disputeCanActivate],
  },
  {
    path: 'credit-info',
    loadChildren: () => import('./features/stripe/stripe.routes').then(r => r.stripeRoutes),
  },
  {
    path: 'report',
    loadChildren: () => import('./features/report/report.routes').then(r => r.reportRoutes),
    canActivate: [authCanActivate, disputeCanActivate],
  },
  {
    path: 'support',
    loadComponent: () => import('./features/support/support.component').then(c => c.SupportComponent),
    data: {showMenu: false}
  },
  {
    path: 'ai-image-bot',
    loadComponent: () => import('./features/ai-image-bot/ai-image-bot.component').then(c => c.AiImageBotComponent),
    canActivate: [authCanActivate],
    data: {showMenu: false, title: 'seo.title.aiImageBot', description: 'seo.description.aiImageBot'},
  },
  {
    path: 'invite/:token',
    loadComponent: () => import('./features/chat/pages/group-invite/group-invite.component').then(c => c.GroupInviteComponent),
    data: {showMenu: false}
  },
  {
    path: 'license-agreement-en.html',
    loadComponent: () => import('./features/docs/license-agreement/license-agreement.component').then(c => c.LicenseAgreementComponent),
    data: {showMenu: false}
  },
  {
    path: 'privacy-policy-en.html',
    loadComponent: () => import('./features/docs/privacy-policy/privacy-policy.component').then(c => c.PrivacyPolicyComponent),
    data: {showMenu: false}
  },
  {
    path: 'cookies-policy-en.html',
    loadComponent: () => import('./features/docs/cookies-policy/cookies-policy.component').then(c => c.CookiesPolicyComponent),
    data: {showMenu: false}
  },
  {
    path: 'child-safety-standards-policy-en.html',
    loadComponent: () => import('./features/docs/child-safety-standards-policy/child-safety-standards-policy.component').then(c => c.ChildSafetyStandardsPolicyComponent),
    data: {showMenu: false}
  },
  {
    path: 'terms-and-conditions-en.html',
    loadComponent: () => import('./features/docs/terms-and-conditions/terms-and-conditions.component').then(c => c.TermsAndConditionsComponent),
    data: {showMenu: false}
  },
  {
    path: 'disclaimer-en.html',
    loadComponent: () => import('./features/docs/disclaimer/disclaimer.component').then(c => c.DisclaimerComponent),
    data: {showMenu: false}
  },
  {
    path: 'extra-work',
    loadChildren: () => import('./features/extra-work/extra-work.routes').then(r => r.extraWorkRoutes),
    canActivate: [authCanActivate],
  },
  {
    path: '404',
    loadComponent: () => import('./features/404/not-found.component').then(c => c.NotFoundComponent),
    data: {showMenu: true, title: 'seo.title.pageNotFound', description: 'seo.description.pageNotFound'}
  },
  {
    matcher: nicknameMatcher,
    loadComponent: () => import('./features/people/user-profile/user-profile.component').then(c => c.UserProfileComponent),
    data: {showMenu: true, title: 'seo.title.people', description: 'seo.description.people'}
  },
  {
    path: '**',
    // Simple redirect to 404 is not an option, because we need to keep URL!
    loadComponent: () => import('./features/404/not-found.component').then(c => c.NotFoundComponent),
    data: {showMenu: true, title: 'seo.title.pageNotFound', description: 'seo.description.pageNotFound'}
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: environment.production ? NoPreloading : PreloadAllModules,
      initialNavigation: 'enabledBlocking'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
